// Country dial codes — DATA ONLY, deliberately.
//
// Every ITU country/territory dial code paired with an ISO 3166-1 alpha-2 code, so
// the claim page's phone field can offer a real country picker without pulling in a
// phone-number package (this app has none, and a metadata blob measured in hundreds
// of kilobytes is not a trade we want on a page factories open on mobile data).
//
// Nothing here computes: no lookup helpers, no flag derivation, no formatting. That
// is what keeps the file swappable for `libphonenumber-js` / `country-telephone-data`
// later by rewriting this one export, with no change to any component. The flag emoji
// a consumer renders comes from `iso` (the regional-indicator pair) — also not stored,
// because it is derivable and a stored copy would only drift.
//
// Shared dial codes are real and intended: +1 covers the whole NANP, +7 Russia and
// Kazakhstan, +44 the UK and its crown dependencies, and +39/+47/+61/+212/+262/+358/
// +590/+599 each cover several territories. A consumer that must turn a dial code
// back into ONE country picks its own primary; this list does not rank them.
//
// Ordered by English name, with accented forms filed under their unaccented letter.
export const COUNTRY_CODES = [
  { iso: 'AF', name_en: 'Afghanistan', name_zh: '阿富汗', dial: '+93' },
  { iso: 'AX', name_en: 'Åland Islands', name_zh: '奥兰群岛', dial: '+358' },
  { iso: 'AL', name_en: 'Albania', name_zh: '阿尔巴尼亚', dial: '+355' },
  { iso: 'DZ', name_en: 'Algeria', name_zh: '阿尔及利亚', dial: '+213' },
  { iso: 'AS', name_en: 'American Samoa', name_zh: '美属萨摩亚', dial: '+1' },
  { iso: 'AD', name_en: 'Andorra', name_zh: '安道尔', dial: '+376' },
  { iso: 'AO', name_en: 'Angola', name_zh: '安哥拉', dial: '+244' },
  { iso: 'AI', name_en: 'Anguilla', name_zh: '安圭拉', dial: '+1' },
  { iso: 'AG', name_en: 'Antigua and Barbuda', name_zh: '安提瓜和巴布达', dial: '+1' },
  { iso: 'AR', name_en: 'Argentina', name_zh: '阿根廷', dial: '+54' },
  { iso: 'AM', name_en: 'Armenia', name_zh: '亚美尼亚', dial: '+374' },
  { iso: 'AW', name_en: 'Aruba', name_zh: '阿鲁巴', dial: '+297' },
  { iso: 'AC', name_en: 'Ascension Island', name_zh: '阿森松岛', dial: '+247' },
  { iso: 'AU', name_en: 'Australia', name_zh: '澳大利亚', dial: '+61' },
  { iso: 'AT', name_en: 'Austria', name_zh: '奥地利', dial: '+43' },
  { iso: 'AZ', name_en: 'Azerbaijan', name_zh: '阿塞拜疆', dial: '+994' },
  { iso: 'BS', name_en: 'Bahamas', name_zh: '巴哈马', dial: '+1' },
  { iso: 'BH', name_en: 'Bahrain', name_zh: '巴林', dial: '+973' },
  { iso: 'BD', name_en: 'Bangladesh', name_zh: '孟加拉国', dial: '+880' },
  { iso: 'BB', name_en: 'Barbados', name_zh: '巴巴多斯', dial: '+1' },
  { iso: 'BY', name_en: 'Belarus', name_zh: '白俄罗斯', dial: '+375' },
  { iso: 'BE', name_en: 'Belgium', name_zh: '比利时', dial: '+32' },
  { iso: 'BZ', name_en: 'Belize', name_zh: '伯利兹', dial: '+501' },
  { iso: 'BJ', name_en: 'Benin', name_zh: '贝宁', dial: '+229' },
  { iso: 'BM', name_en: 'Bermuda', name_zh: '百慕大', dial: '+1' },
  { iso: 'BT', name_en: 'Bhutan', name_zh: '不丹', dial: '+975' },
  { iso: 'BO', name_en: 'Bolivia', name_zh: '玻利维亚', dial: '+591' },
  { iso: 'BA', name_en: 'Bosnia and Herzegovina', name_zh: '波斯尼亚和黑塞哥维那', dial: '+387' },
  { iso: 'BW', name_en: 'Botswana', name_zh: '博茨瓦纳', dial: '+267' },
  { iso: 'BR', name_en: 'Brazil', name_zh: '巴西', dial: '+55' },
  { iso: 'IO', name_en: 'British Indian Ocean Territory', name_zh: '英属印度洋领地', dial: '+246' },
  { iso: 'VG', name_en: 'British Virgin Islands', name_zh: '英属维尔京群岛', dial: '+1' },
  { iso: 'BN', name_en: 'Brunei', name_zh: '文莱', dial: '+673' },
  { iso: 'BG', name_en: 'Bulgaria', name_zh: '保加利亚', dial: '+359' },
  { iso: 'BF', name_en: 'Burkina Faso', name_zh: '布基纳法索', dial: '+226' },
  { iso: 'BI', name_en: 'Burundi', name_zh: '布隆迪', dial: '+257' },
  { iso: 'KH', name_en: 'Cambodia', name_zh: '柬埔寨', dial: '+855' },
  { iso: 'CM', name_en: 'Cameroon', name_zh: '喀麦隆', dial: '+237' },
  { iso: 'CA', name_en: 'Canada', name_zh: '加拿大', dial: '+1' },
  { iso: 'CV', name_en: 'Cape Verde', name_zh: '佛得角', dial: '+238' },
  { iso: 'BQ', name_en: 'Caribbean Netherlands', name_zh: '荷兰加勒比区', dial: '+599' },
  { iso: 'KY', name_en: 'Cayman Islands', name_zh: '开曼群岛', dial: '+1' },
  { iso: 'CF', name_en: 'Central African Republic', name_zh: '中非共和国', dial: '+236' },
  { iso: 'TD', name_en: 'Chad', name_zh: '乍得', dial: '+235' },
  { iso: 'CL', name_en: 'Chile', name_zh: '智利', dial: '+56' },
  { iso: 'CN', name_en: 'China', name_zh: '中国', dial: '+86' },
  { iso: 'CX', name_en: 'Christmas Island', name_zh: '圣诞岛', dial: '+61' },
  { iso: 'CC', name_en: 'Cocos (Keeling) Islands', name_zh: '科科斯群岛', dial: '+61' },
  { iso: 'CO', name_en: 'Colombia', name_zh: '哥伦比亚', dial: '+57' },
  { iso: 'KM', name_en: 'Comoros', name_zh: '科摩罗', dial: '+269' },
  { iso: 'CK', name_en: 'Cook Islands', name_zh: '库克群岛', dial: '+682' },
  { iso: 'CR', name_en: 'Costa Rica', name_zh: '哥斯达黎加', dial: '+506' },
  { iso: 'CI', name_en: "Côte d'Ivoire", name_zh: '科特迪瓦', dial: '+225' },
  { iso: 'HR', name_en: 'Croatia', name_zh: '克罗地亚', dial: '+385' },
  { iso: 'CU', name_en: 'Cuba', name_zh: '古巴', dial: '+53' },
  { iso: 'CW', name_en: 'Curaçao', name_zh: '库拉索', dial: '+599' },
  { iso: 'CY', name_en: 'Cyprus', name_zh: '塞浦路斯', dial: '+357' },
  { iso: 'CZ', name_en: 'Czech Republic', name_zh: '捷克', dial: '+420' },
  { iso: 'CD', name_en: 'DR Congo', name_zh: '刚果（金）', dial: '+243' },
  { iso: 'DK', name_en: 'Denmark', name_zh: '丹麦', dial: '+45' },
  { iso: 'DJ', name_en: 'Djibouti', name_zh: '吉布提', dial: '+253' },
  { iso: 'DM', name_en: 'Dominica', name_zh: '多米尼克', dial: '+1' },
  { iso: 'DO', name_en: 'Dominican Republic', name_zh: '多米尼加', dial: '+1' },
  { iso: 'EC', name_en: 'Ecuador', name_zh: '厄瓜多尔', dial: '+593' },
  { iso: 'EG', name_en: 'Egypt', name_zh: '埃及', dial: '+20' },
  { iso: 'SV', name_en: 'El Salvador', name_zh: '萨尔瓦多', dial: '+503' },
  { iso: 'GQ', name_en: 'Equatorial Guinea', name_zh: '赤道几内亚', dial: '+240' },
  { iso: 'ER', name_en: 'Eritrea', name_zh: '厄立特里亚', dial: '+291' },
  { iso: 'EE', name_en: 'Estonia', name_zh: '爱沙尼亚', dial: '+372' },
  { iso: 'SZ', name_en: 'Eswatini', name_zh: '斯威士兰', dial: '+268' },
  { iso: 'ET', name_en: 'Ethiopia', name_zh: '埃塞俄比亚', dial: '+251' },
  { iso: 'FK', name_en: 'Falkland Islands', name_zh: '福克兰群岛', dial: '+500' },
  { iso: 'FO', name_en: 'Faroe Islands', name_zh: '法罗群岛', dial: '+298' },
  { iso: 'FJ', name_en: 'Fiji', name_zh: '斐济', dial: '+679' },
  { iso: 'FI', name_en: 'Finland', name_zh: '芬兰', dial: '+358' },
  { iso: 'FR', name_en: 'France', name_zh: '法国', dial: '+33' },
  { iso: 'GF', name_en: 'French Guiana', name_zh: '法属圭亚那', dial: '+594' },
  { iso: 'PF', name_en: 'French Polynesia', name_zh: '法属波利尼西亚', dial: '+689' },
  { iso: 'GA', name_en: 'Gabon', name_zh: '加蓬', dial: '+241' },
  { iso: 'GM', name_en: 'Gambia', name_zh: '冈比亚', dial: '+220' },
  { iso: 'GE', name_en: 'Georgia', name_zh: '格鲁吉亚', dial: '+995' },
  { iso: 'DE', name_en: 'Germany', name_zh: '德国', dial: '+49' },
  { iso: 'GH', name_en: 'Ghana', name_zh: '加纳', dial: '+233' },
  { iso: 'GI', name_en: 'Gibraltar', name_zh: '直布罗陀', dial: '+350' },
  { iso: 'GR', name_en: 'Greece', name_zh: '希腊', dial: '+30' },
  { iso: 'GL', name_en: 'Greenland', name_zh: '格陵兰', dial: '+299' },
  { iso: 'GD', name_en: 'Grenada', name_zh: '格林纳达', dial: '+1' },
  { iso: 'GP', name_en: 'Guadeloupe', name_zh: '瓜德罗普', dial: '+590' },
  { iso: 'GU', name_en: 'Guam', name_zh: '关岛', dial: '+1' },
  { iso: 'GT', name_en: 'Guatemala', name_zh: '危地马拉', dial: '+502' },
  { iso: 'GG', name_en: 'Guernsey', name_zh: '根西岛', dial: '+44' },
  { iso: 'GN', name_en: 'Guinea', name_zh: '几内亚', dial: '+224' },
  { iso: 'GW', name_en: 'Guinea-Bissau', name_zh: '几内亚比绍', dial: '+245' },
  { iso: 'GY', name_en: 'Guyana', name_zh: '圭亚那', dial: '+592' },
  { iso: 'HT', name_en: 'Haiti', name_zh: '海地', dial: '+509' },
  { iso: 'HN', name_en: 'Honduras', name_zh: '洪都拉斯', dial: '+504' },
  { iso: 'HK', name_en: 'Hong Kong', name_zh: '中国香港', dial: '+852' },
  { iso: 'HU', name_en: 'Hungary', name_zh: '匈牙利', dial: '+36' },
  { iso: 'IS', name_en: 'Iceland', name_zh: '冰岛', dial: '+354' },
  { iso: 'IN', name_en: 'India', name_zh: '印度', dial: '+91' },
  { iso: 'ID', name_en: 'Indonesia', name_zh: '印度尼西亚', dial: '+62' },
  { iso: 'IR', name_en: 'Iran', name_zh: '伊朗', dial: '+98' },
  { iso: 'IQ', name_en: 'Iraq', name_zh: '伊拉克', dial: '+964' },
  { iso: 'IE', name_en: 'Ireland', name_zh: '爱尔兰', dial: '+353' },
  { iso: 'IM', name_en: 'Isle of Man', name_zh: '马恩岛', dial: '+44' },
  { iso: 'IL', name_en: 'Israel', name_zh: '以色列', dial: '+972' },
  { iso: 'IT', name_en: 'Italy', name_zh: '意大利', dial: '+39' },
  { iso: 'JM', name_en: 'Jamaica', name_zh: '牙买加', dial: '+1' },
  { iso: 'JP', name_en: 'Japan', name_zh: '日本', dial: '+81' },
  { iso: 'JE', name_en: 'Jersey', name_zh: '泽西岛', dial: '+44' },
  { iso: 'JO', name_en: 'Jordan', name_zh: '约旦', dial: '+962' },
  { iso: 'KZ', name_en: 'Kazakhstan', name_zh: '哈萨克斯坦', dial: '+7' },
  { iso: 'KE', name_en: 'Kenya', name_zh: '肯尼亚', dial: '+254' },
  { iso: 'KI', name_en: 'Kiribati', name_zh: '基里巴斯', dial: '+686' },
  { iso: 'XK', name_en: 'Kosovo', name_zh: '科索沃', dial: '+383' },
  { iso: 'KW', name_en: 'Kuwait', name_zh: '科威特', dial: '+965' },
  { iso: 'KG', name_en: 'Kyrgyzstan', name_zh: '吉尔吉斯斯坦', dial: '+996' },
  { iso: 'LA', name_en: 'Laos', name_zh: '老挝', dial: '+856' },
  { iso: 'LV', name_en: 'Latvia', name_zh: '拉脱维亚', dial: '+371' },
  { iso: 'LB', name_en: 'Lebanon', name_zh: '黎巴嫩', dial: '+961' },
  { iso: 'LS', name_en: 'Lesotho', name_zh: '莱索托', dial: '+266' },
  { iso: 'LR', name_en: 'Liberia', name_zh: '利比里亚', dial: '+231' },
  { iso: 'LY', name_en: 'Libya', name_zh: '利比亚', dial: '+218' },
  { iso: 'LI', name_en: 'Liechtenstein', name_zh: '列支敦士登', dial: '+423' },
  { iso: 'LT', name_en: 'Lithuania', name_zh: '立陶宛', dial: '+370' },
  { iso: 'LU', name_en: 'Luxembourg', name_zh: '卢森堡', dial: '+352' },
  { iso: 'MO', name_en: 'Macau', name_zh: '中国澳门', dial: '+853' },
  { iso: 'MG', name_en: 'Madagascar', name_zh: '马达加斯加', dial: '+261' },
  { iso: 'MW', name_en: 'Malawi', name_zh: '马拉维', dial: '+265' },
  { iso: 'MY', name_en: 'Malaysia', name_zh: '马来西亚', dial: '+60' },
  { iso: 'MV', name_en: 'Maldives', name_zh: '马尔代夫', dial: '+960' },
  { iso: 'ML', name_en: 'Mali', name_zh: '马里', dial: '+223' },
  { iso: 'MT', name_en: 'Malta', name_zh: '马耳他', dial: '+356' },
  { iso: 'MH', name_en: 'Marshall Islands', name_zh: '马绍尔群岛', dial: '+692' },
  { iso: 'MQ', name_en: 'Martinique', name_zh: '马提尼克', dial: '+596' },
  { iso: 'MR', name_en: 'Mauritania', name_zh: '毛里塔尼亚', dial: '+222' },
  { iso: 'MU', name_en: 'Mauritius', name_zh: '毛里求斯', dial: '+230' },
  { iso: 'YT', name_en: 'Mayotte', name_zh: '马约特', dial: '+262' },
  { iso: 'MX', name_en: 'Mexico', name_zh: '墨西哥', dial: '+52' },
  { iso: 'FM', name_en: 'Micronesia', name_zh: '密克罗尼西亚', dial: '+691' },
  { iso: 'MD', name_en: 'Moldova', name_zh: '摩尔多瓦', dial: '+373' },
  { iso: 'MC', name_en: 'Monaco', name_zh: '摩纳哥', dial: '+377' },
  { iso: 'MN', name_en: 'Mongolia', name_zh: '蒙古', dial: '+976' },
  { iso: 'ME', name_en: 'Montenegro', name_zh: '黑山', dial: '+382' },
  { iso: 'MS', name_en: 'Montserrat', name_zh: '蒙特塞拉特', dial: '+1' },
  { iso: 'MA', name_en: 'Morocco', name_zh: '摩洛哥', dial: '+212' },
  { iso: 'MZ', name_en: 'Mozambique', name_zh: '莫桑比克', dial: '+258' },
  { iso: 'MM', name_en: 'Myanmar', name_zh: '缅甸', dial: '+95' },
  { iso: 'NA', name_en: 'Namibia', name_zh: '纳米比亚', dial: '+264' },
  { iso: 'NR', name_en: 'Nauru', name_zh: '瑙鲁', dial: '+674' },
  { iso: 'NP', name_en: 'Nepal', name_zh: '尼泊尔', dial: '+977' },
  { iso: 'NL', name_en: 'Netherlands', name_zh: '荷兰', dial: '+31' },
  { iso: 'NC', name_en: 'New Caledonia', name_zh: '新喀里多尼亚', dial: '+687' },
  { iso: 'NZ', name_en: 'New Zealand', name_zh: '新西兰', dial: '+64' },
  { iso: 'NI', name_en: 'Nicaragua', name_zh: '尼加拉瓜', dial: '+505' },
  { iso: 'NE', name_en: 'Niger', name_zh: '尼日尔', dial: '+227' },
  { iso: 'NG', name_en: 'Nigeria', name_zh: '尼日利亚', dial: '+234' },
  { iso: 'NU', name_en: 'Niue', name_zh: '纽埃', dial: '+683' },
  { iso: 'NF', name_en: 'Norfolk Island', name_zh: '诺福克岛', dial: '+672' },
  { iso: 'KP', name_en: 'North Korea', name_zh: '朝鲜', dial: '+850' },
  { iso: 'MK', name_en: 'North Macedonia', name_zh: '北马其顿', dial: '+389' },
  { iso: 'MP', name_en: 'Northern Mariana Islands', name_zh: '北马里亚纳群岛', dial: '+1' },
  { iso: 'NO', name_en: 'Norway', name_zh: '挪威', dial: '+47' },
  { iso: 'OM', name_en: 'Oman', name_zh: '阿曼', dial: '+968' },
  { iso: 'PK', name_en: 'Pakistan', name_zh: '巴基斯坦', dial: '+92' },
  { iso: 'PW', name_en: 'Palau', name_zh: '帕劳', dial: '+680' },
  { iso: 'PS', name_en: 'Palestine', name_zh: '巴勒斯坦', dial: '+970' },
  { iso: 'PA', name_en: 'Panama', name_zh: '巴拿马', dial: '+507' },
  { iso: 'PG', name_en: 'Papua New Guinea', name_zh: '巴布亚新几内亚', dial: '+675' },
  { iso: 'PY', name_en: 'Paraguay', name_zh: '巴拉圭', dial: '+595' },
  { iso: 'PE', name_en: 'Peru', name_zh: '秘鲁', dial: '+51' },
  { iso: 'PH', name_en: 'Philippines', name_zh: '菲律宾', dial: '+63' },
  { iso: 'PL', name_en: 'Poland', name_zh: '波兰', dial: '+48' },
  { iso: 'PT', name_en: 'Portugal', name_zh: '葡萄牙', dial: '+351' },
  { iso: 'PR', name_en: 'Puerto Rico', name_zh: '波多黎各', dial: '+1' },
  { iso: 'QA', name_en: 'Qatar', name_zh: '卡塔尔', dial: '+974' },
  { iso: 'CG', name_en: 'Republic of the Congo', name_zh: '刚果（布）', dial: '+242' },
  { iso: 'RE', name_en: 'Réunion', name_zh: '留尼汪', dial: '+262' },
  { iso: 'RO', name_en: 'Romania', name_zh: '罗马尼亚', dial: '+40' },
  { iso: 'RU', name_en: 'Russia', name_zh: '俄罗斯', dial: '+7' },
  { iso: 'RW', name_en: 'Rwanda', name_zh: '卢旺达', dial: '+250' },
  { iso: 'BL', name_en: 'Saint Barthélemy', name_zh: '圣巴泰勒米', dial: '+590' },
  { iso: 'SH', name_en: 'Saint Helena', name_zh: '圣赫勒拿', dial: '+290' },
  { iso: 'KN', name_en: 'Saint Kitts and Nevis', name_zh: '圣基茨和尼维斯', dial: '+1' },
  { iso: 'LC', name_en: 'Saint Lucia', name_zh: '圣卢西亚', dial: '+1' },
  { iso: 'MF', name_en: 'Saint Martin', name_zh: '法属圣马丁', dial: '+590' },
  { iso: 'PM', name_en: 'Saint Pierre and Miquelon', name_zh: '圣皮埃尔和密克隆', dial: '+508' },
  { iso: 'VC', name_en: 'Saint Vincent and the Grenadines', name_zh: '圣文森特和格林纳丁斯', dial: '+1' },
  { iso: 'WS', name_en: 'Samoa', name_zh: '萨摩亚', dial: '+685' },
  { iso: 'SM', name_en: 'San Marino', name_zh: '圣马力诺', dial: '+378' },
  { iso: 'ST', name_en: 'São Tomé and Príncipe', name_zh: '圣多美和普林西比', dial: '+239' },
  { iso: 'SA', name_en: 'Saudi Arabia', name_zh: '沙特阿拉伯', dial: '+966' },
  { iso: 'SN', name_en: 'Senegal', name_zh: '塞内加尔', dial: '+221' },
  { iso: 'RS', name_en: 'Serbia', name_zh: '塞尔维亚', dial: '+381' },
  { iso: 'SC', name_en: 'Seychelles', name_zh: '塞舌尔', dial: '+248' },
  { iso: 'SL', name_en: 'Sierra Leone', name_zh: '塞拉利昂', dial: '+232' },
  { iso: 'SG', name_en: 'Singapore', name_zh: '新加坡', dial: '+65' },
  { iso: 'SX', name_en: 'Sint Maarten', name_zh: '荷属圣马丁', dial: '+1' },
  { iso: 'SK', name_en: 'Slovakia', name_zh: '斯洛伐克', dial: '+421' },
  { iso: 'SI', name_en: 'Slovenia', name_zh: '斯洛文尼亚', dial: '+386' },
  { iso: 'SB', name_en: 'Solomon Islands', name_zh: '所罗门群岛', dial: '+677' },
  { iso: 'SO', name_en: 'Somalia', name_zh: '索马里', dial: '+252' },
  { iso: 'ZA', name_en: 'South Africa', name_zh: '南非', dial: '+27' },
  { iso: 'KR', name_en: 'South Korea', name_zh: '韩国', dial: '+82' },
  { iso: 'SS', name_en: 'South Sudan', name_zh: '南苏丹', dial: '+211' },
  { iso: 'ES', name_en: 'Spain', name_zh: '西班牙', dial: '+34' },
  { iso: 'LK', name_en: 'Sri Lanka', name_zh: '斯里兰卡', dial: '+94' },
  { iso: 'SD', name_en: 'Sudan', name_zh: '苏丹', dial: '+249' },
  { iso: 'SR', name_en: 'Suriname', name_zh: '苏里南', dial: '+597' },
  { iso: 'SE', name_en: 'Sweden', name_zh: '瑞典', dial: '+46' },
  { iso: 'CH', name_en: 'Switzerland', name_zh: '瑞士', dial: '+41' },
  { iso: 'SY', name_en: 'Syria', name_zh: '叙利亚', dial: '+963' },
  { iso: 'TW', name_en: 'Taiwan', name_zh: '中国台湾', dial: '+886' },
  { iso: 'TJ', name_en: 'Tajikistan', name_zh: '塔吉克斯坦', dial: '+992' },
  { iso: 'TZ', name_en: 'Tanzania', name_zh: '坦桑尼亚', dial: '+255' },
  { iso: 'TH', name_en: 'Thailand', name_zh: '泰国', dial: '+66' },
  { iso: 'TL', name_en: 'Timor-Leste', name_zh: '东帝汶', dial: '+670' },
  { iso: 'TG', name_en: 'Togo', name_zh: '多哥', dial: '+228' },
  { iso: 'TK', name_en: 'Tokelau', name_zh: '托克劳', dial: '+690' },
  { iso: 'TO', name_en: 'Tonga', name_zh: '汤加', dial: '+676' },
  { iso: 'TT', name_en: 'Trinidad and Tobago', name_zh: '特立尼达和多巴哥', dial: '+1' },
  { iso: 'TN', name_en: 'Tunisia', name_zh: '突尼斯', dial: '+216' },
  { iso: 'TR', name_en: 'Turkey', name_zh: '土耳其', dial: '+90' },
  { iso: 'TM', name_en: 'Turkmenistan', name_zh: '土库曼斯坦', dial: '+993' },
  { iso: 'TC', name_en: 'Turks and Caicos Islands', name_zh: '特克斯和凯科斯群岛', dial: '+1' },
  { iso: 'TV', name_en: 'Tuvalu', name_zh: '图瓦卢', dial: '+688' },
  { iso: 'UG', name_en: 'Uganda', name_zh: '乌干达', dial: '+256' },
  { iso: 'UA', name_en: 'Ukraine', name_zh: '乌克兰', dial: '+380' },
  { iso: 'AE', name_en: 'United Arab Emirates', name_zh: '阿联酋', dial: '+971' },
  { iso: 'GB', name_en: 'United Kingdom', name_zh: '英国', dial: '+44' },
  { iso: 'US', name_en: 'United States', name_zh: '美国', dial: '+1' },
  { iso: 'VI', name_en: 'United States Virgin Islands', name_zh: '美属维尔京群岛', dial: '+1' },
  { iso: 'UY', name_en: 'Uruguay', name_zh: '乌拉圭', dial: '+598' },
  { iso: 'UZ', name_en: 'Uzbekistan', name_zh: '乌兹别克斯坦', dial: '+998' },
  { iso: 'VU', name_en: 'Vanuatu', name_zh: '瓦努阿图', dial: '+678' },
  { iso: 'VA', name_en: 'Vatican City', name_zh: '梵蒂冈', dial: '+39' },
  { iso: 'VE', name_en: 'Venezuela', name_zh: '委内瑞拉', dial: '+58' },
  { iso: 'VN', name_en: 'Vietnam', name_zh: '越南', dial: '+84' },
  { iso: 'WF', name_en: 'Wallis and Futuna', name_zh: '瓦利斯和富图纳', dial: '+681' },
  { iso: 'EH', name_en: 'Western Sahara', name_zh: '西撒哈拉', dial: '+212' },
  { iso: 'YE', name_en: 'Yemen', name_zh: '也门', dial: '+967' },
  { iso: 'ZM', name_en: 'Zambia', name_zh: '赞比亚', dial: '+260' },
  { iso: 'ZW', name_en: 'Zimbabwe', name_zh: '津巴布韦', dial: '+263' },
];
